import { Box } from "@mui/material";
import { Fragment } from "react";
import { ActionButton, ChoiceControl } from "../../components/ui";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionArrangement } from "../contracts";
import type { SpecifyControlBinding } from "../specify/types";
import { GridColumnHeaderText } from "./GridColumnHeaderText";
import { GridRowContent } from "./GridRowContent";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupForValue, groupHeading, type VisualGroup } from "../visualGroups";
import {
  GroupColumnHeadings,
  GroupColumns,
  GridRowGroups,
} from "./GroupHeadings";
import { checkBoxStyle, RadioStyle } from "./choiceControlStyles";
import {
  compactGridButtonStyle,
  compactGridCardStyle,
  compactGridListStyle,
  wideGridButtonStyle,
  wideGridCellStyle,
  wideGridColumnHeaderStyle,
  wideGridInstrumentStyle,
  wideGridRowHeaderStyle,
  wideGridTableStyle,
  wideGridViewportStyle,
} from "./gridInstrumentStyle";
import {
  portraitOptionList,
  portraitOptionRow,
  portraitRowHeader,
} from "./portraitStyles";

export type GridChoiceRow = {
  id: string;
  label: string;
  image?: string;
  displayLabel: boolean;
  customPrompt?: string;
};
export type GridChoiceOption = {
  id: number | string;
  label: string;
  disabled: boolean;
  interactionDisabled?: boolean;
};
type GridChoiceMatrixProps = {
  rows: readonly GridChoiceRow[];
  options: readonly GridChoiceOption[];
  rowGroups?: readonly VisualGroup<string>[];
  optionGroups?: readonly VisualGroup<number>[];
  arrangement: QuestionArrangement;
  mode: "button" | "radio" | "checkbox";
  isSelected: (rowId: string, optionId: number | string) => boolean;
  onSelect: (rowId: string, optionId: number | string) => void;
  testId: string;
  specify?: SpecifyControlBinding;
};

const GridChoiceHeaders = ({
  options,
  optionGroups,
  tick,
}: Pick<GridChoiceMatrixProps, "options" | "optionGroups"> & {
  tick: boolean;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <>
      {(tick || Boolean(optionGroups?.length)) && (
        <Box component="thead">
          <GroupColumnHeadings
            values={options.map((option) => option.id)}
            groups={optionGroups}
          />
          {tick && (
            <Box component="tr">
              <Box
                component="th"
                scope="col"
                sx={wideGridRowHeaderStyle(theme)}
              />
              {options.map((option) => (
                <Box
                  component="th"
                  scope="col"
                  key={option.id}
                  sx={wideGridColumnHeaderStyle(theme)}
                >
                  <GridColumnHeaderText text={option.label} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export const GridChoiceMatrix = (props: GridChoiceMatrixProps) => {
  const {
    rows,
    options,
    rowGroups,
    optionGroups,
    arrangement,
    mode,
    isSelected,
    onSelect,
    testId,
    specify,
  } = props;
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const compact = arrangement === "compact";
  const tick = mode !== "button";
  const hasRowGroups = Boolean(rowGroups?.length);
  const grouped = hasRowGroups || Boolean(optionGroups?.length);

  const rowContent = (row: GridChoiceRow) => (
    <GridRowContent row={row} compact={compact} specify={specify} />
  );

  const choice = (
    row: GridChoiceRow,
    option: GridChoiceOption,
    rowIndex: number,
    optionIndex: number,
  ) => {
    const selected = isSelected(row.id, option.id);
    const label = `${row.label}: ${option.label}`;
    /* v8 ignore start */
    const choose = () => {
      if (!option.interactionDisabled) onSelect(row.id, option.id);
    };
    /* v8 ignore end */
    return tick ? (
      <ChoiceControl
        key={option.id}
        mode={mode}
        currentVal={{ text: option.label, display_text: compact }}
        disabled={option.disabled}
        ariaDisabled={option.interactionDisabled}
        isSelected={selected}
        onClick={choose}
        testId={`grid-choice-${rowIndex + 1}-${optionIndex + 1}`}
        controlName={`${testId}-${rowIndex}`}
        controlValue={String(option.id)}
        controlAriaLabel={label}
        controlSx={mode === "radio" ? RadioStyle(theme) : checkBoxStyle(theme)}
        wrapperSx={
          compact
            ? portraitOptionRow(option.disabled)
            : {
                justifyContent: "center",
                minHeight: `${theme.tokens.spacing["5"] * 3}px`,
              }
        }
        showContent={compact}
      />
    ) : (
      <ActionButton
        key={option.id}
        ariaLabel={label}
        ariaPressed={selected}
        testId={`grid-choice-${rowIndex + 1}-${optionIndex + 1}`}
        disabled={option.disabled}
        ariaDisabled={option.interactionDisabled}
        isSelected={selected}
        onClick={choose}
        style={
          compact
            ? compactGridButtonStyle(theme, selected)
            : wideGridButtonStyle(theme, selected)
        }
      >
        {option.label}
      </ActionButton>
    );
  };

  return (
    <Box
      data-grid-ready="true"
      data-testid={testId}
      sx={{
        width: "100%",
        ...(compact
          ? {}
          : wideGridInstrumentStyle(theme, theme.show_grid_lines, grouped)),
      }}
    >
      {compact ? (
        <Box sx={compactGridListStyle(theme)}>
          {rows.map((row, rowIndex) => (
            <Fragment key={row.id}>
              <VisualGroupHeading text={groupHeading(rowGroups, row.id)} />
              <Box sx={compactGridCardStyle(theme)}>
                <Box sx={portraitRowHeader}>{rowContent(row)}</Box>
                <Box
                  sx={
                    tick
                      ? portraitOptionList
                      : {
                          display: "grid",
                          gridTemplateColumns: `repeat(${optionGroups?.length ? Math.max(...optionGroups.map((group) => group.values.length)) : options.length}, minmax(0, 1fr))`,
                          gap: `${theme.tokens.spacing["1"]}px`,
                        }
                  }
                >
                  {options.map((option, optionIndex) => (
                    <Fragment key={option.id}>
                      <VisualGroupHeading
                        text={groupHeading(optionGroups, option.id)}
                      />
                      {Boolean(optionGroups?.length) &&
                      !groupForValue(optionGroups, option.id) ? (
                        <Box sx={{ gridColumn: "1 / -1" }}>
                          {choice(row, option, rowIndex, optionIndex)}
                        </Box>
                      ) : (
                        choice(row, option, rowIndex, optionIndex)
                      )}
                    </Fragment>
                  ))}
                </Box>
              </Box>
            </Fragment>
          ))}
        </Box>
      ) : (
        <Box sx={wideGridViewportStyle}>
          <Box component="table" sx={wideGridTableStyle}>
            <colgroup span={1} style={{ width: "15%" }} />
            <GroupColumns
              values={options.map((option) => option.id)}
              groups={optionGroups}
            />
            <GridChoiceHeaders
              options={options}
              optionGroups={optionGroups}
              tick={tick}
            />
            <GridRowGroups
              rows={rows}
              groups={rowGroups}
              rowId={(row) => row.id}
              columns={options.length + 1}
            >
              {(row, rowIndex) => (
                <Box component="tr">
                  <Box
                    component="th"
                    scope="row"
                    sx={wideGridRowHeaderStyle(theme)}
                  >
                    {rowContent(row)}
                  </Box>
                  {options.map((option, optionIndex) => (
                    <Box
                      component="td"
                      key={option.id}
                      sx={wideGridCellStyle(theme)}
                    >
                      {choice(row, option, rowIndex, optionIndex)}
                    </Box>
                  ))}
                </Box>
              )}
            </GridRowGroups>
          </Box>
        </Box>
      )}
    </Box>
  );
};
